import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, RadixSelect, Skeleton, type SelectOption } from '@/components/ui';
import type { FundingDashboardRangeEnum } from '@/lib/api-admin/types';
import { FundingDashboardKpis } from '../components/FundingDashboardKpis';
import { FundingDashboardChart } from '../components/FundingDashboardChart';
import { FundingDashboardTopCampaigns } from '../components/FundingDashboardTopCampaigns';
import { useFundingDashboardQuery } from '../hooks/useFundingDashboardQuery';

// CUSTOM 은 별도 날짜 입력 UI 가 필요하므로 1차 범위 셀렉터에서는 제외(프리셋만).
const RANGE_PRESETS: ReadonlyArray<Exclude<FundingDashboardRangeEnum, 'CUSTOM'>> = [
  'TODAY',
  'WEEK_7D',
  'MONTH_30D',
  'QUARTER_90D',
];

// 30s polling 기반 자동 갱신 표시. fetching 중이면 펄스(갱신 중), 평시엔 LIVE(자동 갱신 활성).
// (SSE 보조 채널은 Bearer-token 인증 비호환으로 미사용 — useFundingDashboardQuery 주석 참조.)
const LiveBadge = ({ fetching }: { fetching: boolean }) => {
  const { t } = useTranslation('funding');
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
      data-testid="funding-dashboard-live-badge"
      aria-live="polite"
    >
      <span
        className={`inline-block h-2 w-2 rounded-full${fetching ? ' animate-pulse' : ''}`}
        style={{ background: 'var(--status-success-fg)' }}
        aria-hidden="true"
      />
      {t('funding.dashboard.liveBadge')}
    </span>
  );
};

export const FundingDashboardPage = () => {
  const { t } = useTranslation('funding');
  const [range, setRange] = useState<FundingDashboardRangeEnum>('TODAY');

  const query = useFundingDashboardQuery({ range, topN: 5 });

  const rangeOptions: ReadonlyArray<SelectOption> = RANGE_PRESETS.map((r) => ({
    value: r,
    label: t(`funding.dashboard.range_${r}`),
  }));

  return (
    <PageShell
      title={t('funding.dashboard.title')}
      description={t('funding.dashboard.subject')}
      data-testid="funding-dashboard-page"
      actions={
        <div className="flex items-center gap-3">
          <LiveBadge fetching={query.isFetching} />
          <div className="w-[160px]">
            <RadixSelect
              value={range}
              onChange={(v) => setRange(v as FundingDashboardRangeEnum)}
              options={rangeOptions}
              aria-label={t('funding.dashboard.rangeLabel')}
            />
          </div>
        </div>
      }
    >
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-3">
          <Skeleton className="h-[96px] w-full" />
          <Skeleton className="h-[240px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('funding.dashboard.error')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('funding.actions.retry')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <FundingDashboardKpis data={query.data} />
          <FundingDashboardChart data={query.data} />
          <FundingDashboardTopCampaigns data={query.data} />
          <p className="text-right text-[11px] text-[color:var(--content-muted)]">
            {t('funding.dashboard.lastUpdated', {
              at: new Date(query.data.generatedAt).toLocaleString('ko-KR'),
            })}
          </p>
        </div>
      )}
    </PageShell>
  );
};
